import { CommonModule, NgIf } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { FunctionalType } from "src/app/enums/functional-type";
import { AddCatalogItemCapacity } from "src/app/models/catalogs/catalog";
import Swal from "sweetalert2";

@Component({
  selector: "app-capacities-step",
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: "./capacities-step.component.html",
  styleUrls: ["./capacities-step.component.css"],
})
export class CapacitiesStepComponent implements OnInit {
  @Input() public isReadonly: boolean = false;
  @Input() data: any = {};
  @Input() functionalType: FunctionalType | null = null;
  @Output() dataChange = new EventEmitter<any>();

  versionId = 0;

  inventoryEnabled = false;
  initialStock: number | null = null;
  allowOversell = false;

  seatsEnabled = false;
  totalSeats: number | null = 0;
  holdTTLMinutes: number | null = 0;

  preApprovalRequired = false;
  paymentWindowMinutes: number | null = 0;

  /** Errores de validación globales y por campo/área */
  errors: string[] = [];
  errorsMap: Record<string, string[]> = {};

  FunctionalType = FunctionalType;
  ngOnInit(): void {
    if (this.data) {
      this.inventoryEnabled = this.data.inventoryEnabled;
      this.initialStock = this.data.initialStock;
      this.allowOversell = this.data.allowOversell;

      this.seatsEnabled = this.data.seatsEnabled;
      this.totalSeats = this.data.totalSeats;
      this.holdTTLMinutes = this.data.holdTTLMinutes;

      this.preApprovalRequired = this.data.preApprovalRequired;
      this.paymentWindowMinutes = this.data.paymentWindowMinutes;
    }
  }

  private clampIntMin(v: any, allowNull: boolean, minValue: number): number | null {
    if (v === null || v === undefined || v === "") return allowNull ? null : minValue;
    const n = Math.floor(Number(v));
    return isNaN(n) ? (allowNull ? null : minValue) : (n < minValue ? minValue : n);
  }

  private isInt(v: any): v is number {
    return typeof v === "number" && Number.isInteger(v);
  }

  private addError(key: string, msg: string) {
    this.errors.push(msg);
    if (!this.errorsMap[key]) this.errorsMap[key] = [];
    this.errorsMap[key].push(msg);
  }

  onInitialStockChange(v: any) {
    this.initialStock = this.clampIntMin(v, true, 0);
    this.updateData();
  }

  onTotalCapacityChange(v: any) {
    this.totalSeats = this.clampIntMin(v, false, 0);
    this.updateData();
  }

  onReserveTTLChange(v: any) {
    this.holdTTLMinutes = this.clampIntMin(v, false, 1);
    this.updateData();
  }

  onPaymentWindowChange(v: any) {
    this.paymentWindowMinutes = this.clampIntMin(v, false, 5);
    this.updateData();
  }

  toggleInventory() {
    if (!this.inventoryEnabled) {
      this.initialStock = null;
      this.allowOversell = false;
    }
    this.updateData();
  }

  toggleQuotas() {
    if (!this.seatsEnabled) {
      this.totalSeats = 0;
      this.holdTTLMinutes = 1;
    }
    this.updateData();
  }

  toggleApproval() {
    if (!this.preApprovalRequired) this.paymentWindowMinutes = 60;
    this.updateData();
  }

  updateData() {
    const payload: AddCatalogItemCapacity = {
      versionId: this.versionId,
      inventoryEnabled: this.inventoryEnabled,
      initialStock: this.inventoryEnabled ? this.initialStock : null,
      allowOversell: this.inventoryEnabled ? this.allowOversell : false,
      seatsEnabled: this.seatsEnabled,
      totalSeats: this.seatsEnabled ? this.totalSeats : null,
      holdTTLMinutes: this.seatsEnabled ? this.holdTTLMinutes : null,
      preApprovalRequired: this.preApprovalRequired,
      paymentWindowMinutes: this.preApprovalRequired ? this.paymentWindowMinutes : null,
    };
    this.dataChange.emit({ ...payload });
  }

  hasError(path: string): boolean {
    return !!this.errorsMap?.[path]?.length;
  }

  firstError(path: string): string | null {
    return this.errorsMap?.[path]?.[0] ?? null;
  }

  validate(): boolean {
    this.errors = [];
    this.errorsMap = {};

    const isProduct = this.functionalType === FunctionalType.Product;
    if (isProduct && !this.inventoryEnabled) {
      this.addError("inventory.enabled", "Inventario: Para productos debes habilitar Inventario.");
    }

    if (this.inventoryEnabled) {
      if (this.initialStock === null || this.initialStock === undefined || this.initialStock === 0) {
        this.addError("inventory.initialStock", "Inventario: Debes ingresar un stock inicial.");
      } else if (!this.isInt(this.initialStock) || this.initialStock < 0) {
        this.addError("inventory.initialStock", "Inventario: 'Stock inicial' debe ser un número entero ≥ 0.");
      }
    }

    const isCourse = this.functionalType === FunctionalType.Course;
    if (isCourse) {
      if (!this.seatsEnabled) {
        this.addError("seats.enabled", "Cupos: Para cursos, debes habilitar cupos.");
      }
    }

    if (this.seatsEnabled) {
      if (this.totalSeats === null || !this.isInt(this.totalSeats) || this.totalSeats <= 0) {
        this.addError("seats.totalSeats", "Cupos: 'Total de cupos' debe ser un entero > 0.");
      }
      if (this.holdTTLMinutes === null || !this.isInt(this.holdTTLMinutes) || this.holdTTLMinutes < 1) {
        this.addError("seats.holdTTLMinutes", "Cupos: 'Tiempo de reserva (min)' debe ser un entero ≥ 1.");
      }
    }

    if (this.preApprovalRequired) {
      if (
        this.paymentWindowMinutes === null ||
        !this.isInt(this.paymentWindowMinutes) ||
        this.paymentWindowMinutes < 5
      ) {
        this.addError("approval.paymentWindowMinutes", "Aprobación: 'Ventana de pago (min)' debe ser un entero ≥ 5.");
      }

      if (
        this.seatsEnabled &&
        this.holdTTLMinutes != null &&
        this.paymentWindowMinutes != null &&
        this.isInt(this.holdTTLMinutes) &&
        this.isInt(this.paymentWindowMinutes) &&
        this.holdTTLMinutes >= this.paymentWindowMinutes
      ) {
        this.addError(
          "approval.consistency",
          "Aprobación: 'Tiempo de reserva' debería ser menor que la 'Ventana de pago'."
        );
      }
    }

    this.updateData();

    if (this.errors.length > 0) {
      const htmlList = `<ul style="text-align:left;">${this.errors
        .map((e) => `<li>${e}</li>`)
        .join("")}</ul>`;

      Swal.fire({
        icon: "error",
        title: "Errores de validación",
        html: htmlList,
        confirmButtonText: "Corregir"
      });
    }

    return this.errors.length === 0;
  }
}